import React from "react";
import { Link } from "react-router-dom";
import { ITableTh } from "../../common/table/Table";
import { IOrder } from "../../../model/IOrder";
import parseISOToString from "../../../utils/parseISOToString";
import { getVendorListFromOrderItems } from "../../../utils/getVendorListFromOrderItems";

// --
// -- Column titles are the CS portal's, verbatim - "Order#", "Stock#",
// -- "Create", "Shipped", "Update" and the rest - so the two portals read the
// -- same way and anyone moving between them is not re-learning the table.
// --

/** Unshipped Orders. Sortable: the endpoint is a JPA Specification. */
export const UNSHIPPED_COLUMNS: ITableTh[] = [
  { title: 'Order#', sortCol: 'orderId' },
  { title: 'Vendor' },
  { title: 'Stock#' },
  { title: 'Invoice', sortCol: 'invoice' },
  { title: 'Street', sortCol: 'siStreet' },
  { title: 'City', sortCol: 'siCity' },
  { title: 'Name', sortCol: 'siName' },
  { title: 'Zip', sortCol: 'siZip' },
  { title: 'State', sortCol: 'siState' },
  { title: 'Phone', sortCol: 'siPhone' },
  { title: 'Create', sortCol: 'timeCreate' },
  { title: 'Update', sortCol: 'timeUpdate' },
];

// --
// -- Late Orders. The same titles the CS portal shows, but deliberately NOT
// -- sortable: that endpoint is a native query carrying its own ORDER BY, and
// -- a Pageable sort is appended to the raw SQL by property name, so clicking
// -- a header would ask the database for a column that does not exist.
// --
export const LATE_COLUMNS: ITableTh[] = [
  { title: 'Order#' },
  { title: 'Vendor' },
  { title: 'Stock#' },
  { title: 'Invoice' },
  { title: 'Street' },
  { title: 'City' },
  { title: 'Name' },
  { title: 'Zip' },
  { title: 'State' },
  { title: 'Phone' },
  { title: 'Carrier' },
  { title: 'Create' },
  { title: 'Shipped' },
  { title: 'Update' },
];

const day = (iso?: string | null) => parseISOToString(iso || '');

// --
// -- A real link, so the order opens on a plain click too, and so the row is
// -- reachable by keyboard - the double-click on the row itself is not.
// --
const orderLink = (orderId: string) => React.createElement(
  Link,
  {
    to: `/orders/${orderId}`,
    className: 'text-primary underline underline-offset-2',
    // The row opens on double click; without this the link would swallow the
    // first click and navigate before the second arrives - same result here,
    // but it keeps the two paths independent.
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
  },
  orderId,
);

export const unshippedRow = (order: IOrder): React.ReactNode[] => [
  orderLink(order.orderId),
  getVendorListFromOrderItems(order.orderItems),
  order.itemsStockNumbers,
  order.invoice,
  order.siStreet,
  order.siCity,
  order.siName,
  order.siZip,
  order.siState,
  order.siPhone,
  day(order.timeCreate),
  day(order.timeUpdate),
];

export const lateRow = (order: IOrder): React.ReactNode[] => [
  orderLink(order.orderId),
  getVendorListFromOrderItems(order.orderItems),
  order.itemsStockNumbers,
  order.invoice,
  order.siStreet,
  order.siCity,
  order.siName,
  order.siZip,
  order.siState,
  order.siPhone,
  order.siCarrier,
  day(order.timeCreate),
  day(order.siDate),
  day(order.timeUpdate),
];
